import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2, MessageCircle } from "lucide-react";
import { BUSINESS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please fill name, phone and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "contact_form" });
      toast.success("Thank you! We'll reach out within 24 hours.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to submit. Please call us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-pad bg-navy text-white relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-blue/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-orange/15 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange mb-3">
            Let's Talk
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight">
            Get Your Free Marketing Consultation
          </h2>
          <p className="mt-4 text-slate-300 text-base">
            Tell us about your project. We'll come back within 24 hours with a tailored lead-generation plan.
          </p>

          <div className="mt-8 space-y-4">
            <a
              href={`tel:${BUSINESS.phone}`}
              data-testid="contact-phone-link"
              className="flex items-center gap-4 group"
            >
              <div className="w-12 h-12 rounded-xl bg-white/10 group-hover:bg-orange transition-colors flex items-center justify-center">
                <Phone size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">Call us</div>
                <div className="font-bold text-lg">{BUSINESS.phone}</div>
              </div>
            </a>

            <a
              href={`mailto:${BUSINESS.email}`}
              data-testid="contact-email-link"
              className="flex items-center gap-4 group"
            >
              <div className="w-12 h-12 rounded-xl bg-white/10 group-hover:bg-orange transition-colors flex items-center justify-center">
                <Mail size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">Email us</div>
                <div className="font-bold text-base break-all">{BUSINESS.email}</div>
              </div>
            </a>

            <a
              href={BUSINESS.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="flex items-center gap-4 group"
            >
              <div className="w-12 h-12 rounded-xl bg-white/10 group-hover:bg-green-500 transition-colors flex items-center justify-center">
                <MessageCircle size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">WhatsApp</div>
                <div className="font-bold text-base">Chat with us now</div>
              </div>
            </a>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <MapPin size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">Location</div>
                <div className="font-bold text-base">{BUSINESS.location}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="bg-white rounded-3xl p-6 sm:p-8 text-navy shadow-2xl"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Name *</label>
                <input
                  data-testid="contact-name-input"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  placeholder="Your full name"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-all text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Phone *</label>
                <input
                  data-testid="contact-phone-input"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  required
                  placeholder="+91 90000 00000"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-all text-sm"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Email *</label>
              <input
                data-testid="contact-email-input"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                required
                placeholder="you@company.com"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-all text-sm"
              />
            </div>
            <div className="mt-4">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Message</label>
              <textarea
                data-testid="contact-message-input"
                name="message"
                value={form.message}
                onChange={onChange}
                rows={4}
                placeholder="Tell us about your project, location, and goals..."
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-all text-sm resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-orange hover:bg-[var(--orange-dark)] disabled:opacity-60 text-white font-bold px-6 py-4 rounded-xl transition-all btn-shine shadow-lg shadow-orange/20"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Request Free Consultation
                </>
              )}
            </button>

            <p className="mt-3 text-xs text-slate-500 text-center">
              By submitting, you agree to be contacted by Digital Hights regarding your inquiry.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};
